import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  Search, Type, Heading1, Heading2, Heading3, List, ListOrdered, ListChecks, Quote,
  Code2, Minus, Table2, Bold, Italic, Strikethrough, Link2, Highlighter,
  Paperclip, AtSign, Smile, CheckSquare, Megaphone, MessagesSquare, Lightbulb, Bell, Sparkles,
} from 'lucide-react'

/**
 * Composer "+" ve "/" komut menüsü (ClickUp deseni).
 * Öğeler ya metin kutusuna markdown ekler (ins) ya da bir eylem tetikler (act).
 *
 * ins: { before, after, block } — seçili metni sarar; block=true ise satır başına yazar
 */
export const SLASH_GROUPS = [
  {
    title: 'Ekle',
    items: [
      { id: 'file', label: 'Dosya / görsel', icon: Paperclip, act: 'file', hint: 'sürükle-bırak da olur' },
      { id: 'mention', label: 'Kişi etiketle', icon: AtSign, ins: { before: '@' } },
      { id: 'task', label: 'Görev bağla', icon: CheckSquare, act: 'task', hint: '@@' },
      { id: 'emoji', label: 'Emoji', icon: Smile, act: 'emoji' },
      { id: 'ai', label: 'Kanal asistanı', icon: Sparkles, act: 'ai' },
    ],
  },
  {
    title: 'Gönderi türü',
    items: [
      { id: 'p-announcement', label: 'Duyuru', icon: Megaphone, act: 'post:announcement' },
      { id: 'p-discussion', label: 'Tartışma', icon: MessagesSquare, act: 'post:discussion' },
      { id: 'p-idea', label: 'Fikir', icon: Lightbulb, act: 'post:idea' },
      { id: 'p-update', label: 'Güncelleme', icon: Bell, act: 'post:update' },
    ],
  },
  {
    title: 'Metin',
    items: [
      { id: 'h1', label: 'Başlık 1', icon: Heading1, ins: { before: '# ', block: true } },
      { id: 'h2', label: 'Başlık 2', icon: Heading2, ins: { before: '## ', block: true } },
      { id: 'h3', label: 'Başlık 3', icon: Heading3, ins: { before: '### ', block: true } },
      { id: 'p', label: 'Normal metin', icon: Type, ins: { before: '', block: true } },
      { id: 'ul', label: 'Madde listesi', icon: List, ins: { before: '- ', block: true } },
      { id: 'ol', label: 'Numaralı liste', icon: ListOrdered, ins: { before: '1. ', block: true } },
      { id: 'todo', label: 'Kontrol listesi', icon: ListChecks, ins: { before: '- [ ] ', block: true } },
      { id: 'quote', label: 'Alıntı', icon: Quote, ins: { before: '> ', block: true } },
    ],
  },
  {
    title: 'Biçim',
    items: [
      { id: 'b', label: 'Kalın', icon: Bold, ins: { before: '**', after: '**' }, hint: '⌘B' },
      { id: 'i', label: 'İtalik', icon: Italic, ins: { before: '*', after: '*' }, hint: '⌘I' },
      { id: 's', label: 'Üstü çizili', icon: Strikethrough, ins: { before: '~~', after: '~~' } },
      { id: 'code', label: 'Satır içi kod', icon: Code2, ins: { before: '`', after: '`' } },
      { id: 'mark', label: 'Vurgu', icon: Highlighter, ins: { before: '==', after: '==' } },
      { id: 'link', label: 'Bağlantı', icon: Link2, ins: { before: '[', after: '](https://)' } },
    ],
  },
  {
    title: 'Blok',
    items: [
      { id: 'codeblock', label: 'Kod bloğu', icon: Code2, ins: { before: '```\n', after: '\n```', block: true } },
      { id: 'divider', label: 'Ayırıcı', icon: Minus, ins: { before: '---', block: true } },
      { id: 'table', label: 'Tablo', icon: Table2, ins: { before: '| Başlık | Başlık |\n| --- | --- |\n| değer | değer |', block: true } },
    ],
  },
]

export default function SlashMenu({ x, y, onPick, onClose, query = '' }) {
  const ref = useRef(null)
  const [q, setQ] = useState(query)
  const [pos, setPos] = useState({ left: x, top: y })

  useLayoutEffect(() => {
    const el = ref.current; if (!el) return
    const r = el.getBoundingClientRect()
    let left = x, top = y
    if (left + r.width > window.innerWidth - 8) left = Math.max(8, window.innerWidth - r.width - 8)
    if (top + r.height > window.innerHeight - 8) top = Math.max(8, y - r.height - 12)
    setPos({ left, top })
  }, [x, y])

  useEffect(() => {
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose?.() }
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose?.() } }
    document.addEventListener('mousedown', onDown, true)
    document.addEventListener('keydown', onKey, true)
    return () => { document.removeEventListener('mousedown', onDown, true); document.removeEventListener('keydown', onKey, true) }
  }, [onClose])

  const groups = useMemo(() => {
    const t = q.trim().toLocaleLowerCase('tr')
    if (!t) return SLASH_GROUPS
    return SLASH_GROUPS
      .map((g) => ({ ...g, items: g.items.filter((it) => it.label.toLocaleLowerCase('tr').includes(t)) }))
      .filter((g) => g.items.length)
  }, [q])

  return createPortal(
    <div ref={ref} style={{ left: pos.left, top: pos.top }}
      className="fixed z-[92] w-[420px] max-h-[420px] overflow-y-auto bg-white border border-gray-200 rounded-2xl shadow-2xl p-2">
      <div className="relative mb-1.5 sticky top-0 bg-white pb-1">
        <Search size={13} className="absolute left-2.5 top-2 text-gray-400" />
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ara…"
          className="w-full pl-7 pr-2 py-1.5 text-sm border border-gray-200 rounded-lg outline-none focus:ring-1 focus:ring-violet-200" />
      </div>
      {groups.map((g) => (
        <div key={g.title} className="mb-1.5">
          <div className="px-2 py-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">{g.title}</div>
          <div className="grid grid-cols-2 gap-0.5">
            {g.items.map((it) => (
              <button key={it.id} onClick={() => { onPick?.(it); onClose?.() }}
                className="flex items-center gap-2 px-2 py-1.5 rounded-lg text-left hover:bg-gray-50">
                <span className="w-6 h-6 rounded-md border border-gray-200 flex items-center justify-center text-gray-500 shrink-0">
                  <it.icon size={13} />
                </span>
                <span className="text-[13px] text-gray-800 truncate flex-1">{it.label}</span>
                {it.hint && <span className="text-[10px] text-gray-300 shrink-0">{it.hint}</span>}
              </button>
            ))}
          </div>
        </div>
      ))}
      {groups.length === 0 && <div className="px-3 py-6 text-center text-[12px] text-gray-400">Sonuç yok</div>}
    </div>,
    document.body,
  )
}
